import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ParentDashboardMockup } from '@/components/ParentDashboardMockup';
import { ParentVisibilitySection } from '@/components/ParentVisibilitySection';
import { ParentProgressSection } from '@/components/ParentProgressSection';
import { PaymentsFeatureSection } from '@/components/PaymentsFeatureSection';
import { CourseHookStrip } from '@/components/marketing/CourseHookStrip';
import { RoleAnswers, RoleClose, RoleHead } from '@/components/marketing/roleKit';
import { MARKETING_CTAS } from '@/lib/marketingCtas';

export const PARENTS_ANSWERS = [
  {
    q: 'What can I see?',
    a: "Once your student links you, you see their sessions and the tutor's feedback after each one. Your student chooses whether you see sessions and whether you join the tutor chat, and can revoke the link at any time.",
  },
  {
    q: 'How do I link to my student?',
    a: 'Your student creates an invite code in their account. You enter it in yours. The code works once and expires after 7 days.',
  },
  {
    q: 'Can I book or pay for sessions?',
    a: "Yes, for a linked student, when their settings allow it. Whatever you do for them is logged and they are told. Cancelling is off by default.",
  },
  {
    q: 'Can I see their SAT course progress?',
    a: 'Not today. The parent view covers tutoring sessions, feedback and payments. The SAT course is free for your student to use, and they can show you their course map.',
  },
  {
    q: 'Who are the tutors?',
    a: 'Tutor accounts are set up by the Mentara team, not self-registered. Your student is matched with an available tutor when a session is requested.',
  },
] as const;

export const ParentsLandingPage = () => (
  <>
    <RoleHead
      title="Help Your Child Improve Grades | Personalized Tutoring for Parents | Mentara"
      description="Help your child with personalized online tutoring, and follow their sessions and tutor feedback from a parent account. The free SAT course needs no account."
      keywords="help child improve grades, build confidence, personalized academic support, parent account, tutoring for parents, free SAT course"
      path="/what-we-do/parents/"
    />

    {/* Starting Line: text left, the parent's own dashboard on a sage ground. */}
    <section aria-labelledby="parents-title" className="mx-auto w-full max-w-6xl px-4 pb-12 pt-10 sm:px-6 lg:pb-16 lg:pt-14">
      <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,30rem)] lg:gap-16">
        <div className="flex flex-col items-start gap-5">
          <h1 id="parents-title" className="text-balance text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
            Know what happened in tutoring, without asking.
          </h1>
          <p className="max-w-lg text-pretty text-lg leading-relaxed text-muted-foreground">
            Link to your student's account and see their upcoming sessions and what the tutor wrote afterwards. They choose what you see.
          </p>
          <Button asChild size="lg" className="mt-1 h-12 w-full px-8 text-base sm:w-auto">
            <Link to={MARKETING_CTAS.parents.href}>{MARKETING_CTAS.parents.label}</Link>
          </Button>
        </div>
        <figure className="min-w-0">
          <div className="rounded-3xl border border-emerald-700/20 bg-emerald-600/[0.07] p-3 dark:border-border dark:bg-muted/20 sm:p-5" data-testid="hero-artifact">
            <ParentDashboardMockup />
          </div>
          <figcaption className="mt-3 text-sm text-muted-foreground">Example parent dashboard.</figcaption>
        </figure>
      </div>
    </section>

    <CourseHookStrip audience="parents" />
    <ParentVisibilitySection />
    <ParentProgressSection />
    <PaymentsFeatureSection audience="parents" />
    <RoleAnswers audience="parents" answers={PARENTS_ANSWERS} />
    <RoleClose
      audience="parents"
      heading="Stay in the loop, your student stays in charge."
      body="Create a parent account, then ask your student for an invite code."
      primary={MARKETING_CTAS.parents}
      secondary={{ label: 'How it works for parents', to: '/how-it-works/parents/' }}
    />
  </>
);
